<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import { type Estimate, estimatesApi } from '@/api/entities/review/estimates'

definePage({
  meta: {
    title: '견적서 목록',
    action: 'read',
    subject: 'review',
  },
})

const { handleError } = useErrorHandler()

// 상태
const loading = ref(false)
const estimates = ref<Estimate[]>([])
const page = ref(1)
const itemsPerPage = ref(20)
const totalItems = ref(0)
const query = ref('')
const sortBy = ref<any[]>([{ key: 'id', order: 'desc' }])

// 테이블 헤더
const headers = [
  { title: '견적서 ID', key: 'id', sortable: true },
  { title: '업체명', key: 'company_name', sortable: true },
  { title: '통신판매업 신고번호', key: 'approval_number', sortable: true },
  { title: '사업자 등록번호', key: 'business_number', sortable: true },
  { title: '작성자', key: 'user_name', sortable: false },
  { title: '작성일', key: 'created_at', sortable: true },
  { title: '유효일', key: 'valid_until', sortable: true },
]

// 날짜 포맷
const formatDate = (value: string) => {
  if (!value)
    return '-'

  return new Intl.DateTimeFormat('ko-KR', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(value))
}

// 데이터 로드
const loadEstimates = async () => {
  loading.value = true
  try {
    const { success, data } = await estimatesApi.listEstimates({
      page: page.value,
      per_page: itemsPerPage.value,
      query: query.value || undefined,
      sort: sortBy.value[0]?.key || 'id',
      order: sortBy.value[0]?.order || 'desc',
    })

    if (success && data) {
      estimates.value = (data.data || []).map((item: Estimate) => ({
        ...item,
        user_name: item.user?.name || '-',
      }))
      totalItems.value = data.total || 0
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    loading.value = false
  }
}

const router = useRouter()

// 검색 디바운스
const debouncedSearch = useDebounceFn(() => {
  page.value = 1
  loadEstimates()
}, 500)

// 견적서 작성 페이지로 이동
const goToCreate = () => {
  router.push({ name: 'review-estimates-create' })
}

// 페이지 변경
const onPageChange = (newPage: number) => {
  page.value = newPage
  loadEstimates()
}

// 감시자
watch(query, () => {
  debouncedSearch()
})

watch(sortBy, () => {
  loadEstimates()
}, { deep: true })

onMounted(() => {
  loadEstimates()
})
</script>

<template>
  <VCard>
    <VCardText>
      <!-- 검색 및 액션 -->
      <VRow class="mb-4">
        <VCol
          cols="12"
          md="3"
        >
          <VTextField
            v-model="query"
            label="검색어 입력"
            density="compact"
            prepend-inner-icon="tabler-search"
            clearable
            hide-details
          />
        </VCol>
        <VCol class="d-flex justify-end">
          <VBtn
            color="primary"
            @click="goToCreate"
          >
            견적서 작성
          </VBtn>
        </VCol>
      </VRow>

      <!-- 테이블 -->
      <VDataTableServer
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        v-model:sort-by="sortBy"
        :headers="headers"
        :items="estimates"
        :items-length="totalItems"
        :loading="loading"
        class="elevation-0"
        @update:page="onPageChange"
      >
        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template #item.valid_until="{ item }">
          {{ formatDate(item.valid_until) }}
        </template>
      </VDataTableServer>

      <!-- 총 개수 -->
      <VRow class="mt-4">
        <VCol>
          <span class="text-caption">총 {{ totalItems }}개</span>
        </VCol>
      </VRow>
    </VCardText>
  </VCard>
</template>
